import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js';

/** Shared, deliberately small procedural art kit. All resources have one owner. */
export class WorkshopArt {
  private geometries = new Set<THREE.BufferGeometry>();
  private materials = new Map<number, THREE.MeshStandardMaterial>();
  private textures: THREE.Texture[] = [];
  private labelMaterials: THREE.Material[] = [];
  private cube: RoundedBoxGeometry;
  constructor(radius = .06) { this.cube=new RoundedBoxGeometry(1,1,1,3,radius);this.geometries.add(this.cube); }
  material(color: number) {
    if (!this.materials.has(color)) this.materials.set(color, new THREE.MeshStandardMaterial({ color, roughness: .72 }));
    return this.materials.get(color)!;
  }
  box(parent: THREE.Object3D, color: number, size: [number,number,number], position: [number,number,number]) {
    const mesh = new THREE.Mesh(this.cube, this.material(color));
    mesh.scale.set(size[0], size[1], size[2]);
    mesh.position.set(position[0], position[1], position[2]);
    mesh.castShadow = mesh.receiveShadow = true;
    parent.add(mesh); return mesh;
  }
  sphere(parent: THREE.Object3D, color: number, radius: number, position: [number,number,number]) {
    const geometry = new THREE.IcosahedronGeometry(radius, 1); this.geometries.add(geometry);
    const mesh = new THREE.Mesh(geometry, this.material(color));
    mesh.position.set(position[0], position[1], position[2]); mesh.castShadow = true; parent.add(mesh); return mesh;
  }
  label(parent: THREE.Object3D, text: string, width: number, height: number, position: [number,number,number], background = '#fff1d6', ink = '#203149') {
    const canvas = document.createElement('canvas'); canvas.width = 512; canvas.height = 256;
    const ctx = canvas.getContext('2d')!;
    const texture = new THREE.CanvasTexture(canvas); texture.colorSpace = THREE.SRGBColorSpace;
    this.textures.push(texture);
    const material = new THREE.MeshBasicMaterial({ map: texture }); this.labelMaterials.push(material);
    const geometry = new THREE.PlaneGeometry(width, height); this.geometries.add(geometry);
    const mesh = new THREE.Mesh(geometry, material); mesh.position.set(position[0], position[1], position[2]); parent.add(mesh);
    const setText = (value: string) => {
      ctx.fillStyle = background; ctx.fillRect(0, 0, 512, 256);
      ctx.fillStyle = ink; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.font = `900 ${value.length > 12 ? 38 : value.length > 4 ? 58 : 154}px system-ui`;
      ctx.fillText(value, 256, 133, 476); texture.needsUpdate = true;
    };
    setText(text); return { mesh, setText };
  }
  crate(parent: THREE.Object3D, x: number, y: number, z: number, scale = 1) {
    const g = new THREE.Group(); g.position.set(x,y,z); g.scale.setScalar(scale); parent.add(g);
    this.box(g, 0xb97841, [.9,.9,.9], [0,.45,0]);
    this.box(g, 0xe3b27a, [.17,.025,.91], [0,.913,0]);
    for (const side of [-1,1]) this.box(g, 0xd89a5d, [.09,.9,.025], [side*.39,.45,.463]);
    this.box(g, 0xe9cda4, [.2,.3,.012], [.19,.63,.48]); return g;
  }
  batchStatic(parent: THREE.Object3D) {
    parent.updateMatrixWorld(true);
    const batches = new Map<THREE.Material, THREE.Mesh[]>();
    parent.traverse(object => {
      if(object instanceof THREE.Mesh && object.geometry===this.cube) {
        const material=object.material as THREE.Material;
        const batch=batches.get(material) ?? []; batch.push(object); batches.set(material,batch);
      }
    });
    const inverse = parent.matrixWorld.clone().invert();
    batches.forEach((meshes,material)=>{
      const batch=new THREE.InstancedMesh(this.cube,material,meshes.length);
      meshes.forEach((mesh,index)=>{batch.setMatrixAt(index,inverse.clone().multiply(mesh.matrixWorld));mesh.removeFromParent();});
      batch.castShadow=batch.receiveShadow=true; parent.add(batch);
    });
  }
  dispose() { this.geometries.forEach(g => g.dispose()); this.materials.forEach(m => m.dispose()); this.textures.forEach(t => t.dispose()); this.labelMaterials.forEach(m => m.dispose()); }
}
